import { useMemo, useState } from 'react';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import {
  BadgeCheck,
  Bug,
  ChevronRight,
  Lock,
  Rocket,
  Sparkles,
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { getChangelogs, type ChangelogEntry } from '@/lib/changelog-loader';
import { usePermissions } from '@/lib/hooks/use-permissions';
import { cn } from '@/lib/utils/cn';
import { Badge } from '@/ui-components/ui/badge';
import { Card, CardContent } from '@/ui-components/ui/card';
import { PageHeader, PageTitle } from '@/util-components/page-layout/page-layout';
import { DataViewToolbar } from '@/util-components/data-view/data-view-toolbar';

const CHANGE_TYPE_STYLES: Record<
  ChangelogEntry['types'][number],
  { label: string; icon: typeof Rocket; className: string }
> = {
  feature: {
    label: 'Feature',
    icon: Sparkles,
    className: 'border-blue-100 bg-blue-50/50 text-blue-700',
  },
  improvement: {
    label: 'Verbesserung',
    icon: BadgeCheck,
    className: 'border-emerald-100 bg-emerald-50/50 text-emerald-700',
  },
  fix: {
    label: 'Fix',
    icon: Bug,
    className: 'border-amber-100 bg-amber-50/50 text-amber-700',
  },
  breaking: {
    label: 'Breaking',
    icon: ChevronRight,
    className: 'border-rose-100 bg-rose-50/50 text-rose-700',
  },
  security: {
    label: 'Security',
    icon: Lock,
    className: 'border-violet-100 bg-violet-50/50 text-violet-700',
  },
};

const RELEASE_CARD_MARKDOWN = {
  h1: ({ node: _node, children, ...props }: any) => (
    <h3 {...props} className="text-lg font-semibold tracking-tight text-gray-900">
      {children}
    </h3>
  ),
  h2: ({ node: _node, children, ...props }: any) => (
    <h3
      {...props}
      className="mt-8 border-t border-gray-100 pt-6 text-base font-semibold tracking-tight text-gray-900 first:mt-0 first:border-t-0 first:pt-0"
    >
      {children}
    </h3>
  ),
  h3: ({ node: _node, children, ...props }: any) => (
    <h4 {...props} className="mt-6 text-sm font-semibold uppercase tracking-[0.16em] text-gray-500">
      {children}
    </h4>
  ),
  p: ({ node: _node, ...props }: any) => (
    <p {...props} className="text-sm leading-relaxed text-gray-500" />
  ),
  strong: ({ node: _node, ...props }: any) => (
    <strong {...props} className="font-semibold text-gray-900" />
  ),
  em: ({ node: _node, ...props }: any) => (
    <em {...props} className="text-gray-700" />
  ),
  ul: ({ node: _node, ...props }: any) => (
    <ul {...props} className="space-y-3" />
  ),
  ol: ({ node: _node, ...props }: any) => (
    <ol {...props} className="space-y-3 pl-5" />
  ),
  li: ({ node: _node, children, ...props }: any) => (
    <li {...props} className="flex items-start gap-3 text-sm leading-relaxed text-gray-500">
      <span className="mt-2.5 h-1.5 w-1.5 shrink-0 rounded-full bg-gray-300" aria-hidden="true" />
      <span className="min-w-0 flex-1">{children}</span>
    </li>
  ),
  img: ({ src, alt, ...props }: any) => {
    const imageSrc = normalizeImageSrc(src);
    if (!imageSrc) {
      return null;
    }

    return (
      <img
        {...props}
        src={imageSrc}
        alt={alt || 'Changelog Bild'}
        className="w-full max-h-[500px] md:max-w-[500px] rounded-2xl border border-gray-200 bg-gray-50 object-cover shadow-sm"
        loading="lazy"
      />
    );
  },
  a: ({ node: _node, children, ...props }: any) => (
    <a
      {...props}
      className="font-medium text-blue-600 underline decoration-blue-200 underline-offset-4 transition-colors hover:text-blue-700"
      target="_blank"
      rel="noopener noreferrer"
    >
      {children}
    </a>
  ),
  hr: () => <div className="my-6 border-t border-gray-100" />,
  blockquote: ({ node: _node, ...props }: any) => (
    <blockquote
      {...props}
      className="border-l-2 border-gray-200 pl-4 text-sm leading-relaxed text-gray-500 italic"
    />
  ),
};

function normalizeImageSrc(src?: string) {
  if (!src) {
    return '';
  }

  if (src.startsWith('/assets/')) {
    return src;
  }

  if (src.startsWith('assets/')) {
    return `/${src}`;
  }

  if (src.startsWith('changelog-assets/')) {
    return `/${src}`;
  }

  if (src.startsWith('/changelog-assets/')) {
    return src;
  }

  return src;
}

function ReleaseTypeBadge({ type }: { type: ChangelogEntry['types'][number] }) {
  const config = CHANGE_TYPE_STYLES[type];
  const Icon = config.icon;

  return (
    <Badge
      variant="outline"
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[11px] font-medium shadow-sm',
        config.className,
      )}
    >
      <Icon className="h-3 w-3" />
      {config.label}
    </Badge>
  );
}

function EmptyState() {
  return (
    <Card className="border-dashed border-gray-200 bg-white shadow-sm rounded-2xl">
      <CardContent className="flex flex-col items-center justify-center px-6 py-16 text-center">
        <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl border border-gray-100 bg-gray-50 text-gray-500">
          <Rocket className="h-5 w-5" />
        </div>
        <h2 className="text-base font-semibold text-gray-900">Noch keine Updates verfügbar.</h2>
        <p className="mt-2 max-w-md text-sm leading-6 text-gray-500">
          Neue Releases und Verbesserungen erscheinen hier, sobald sie veröffentlicht wurden.
        </p>
      </CardContent>
    </Card>
  );
}

const ChangelogPage = () => {
  const { isInternal } = usePermissions();

  const audience = useMemo<'customer' | 'internal'>(
    () => (isInternal ? 'internal' : 'customer'),
    [isInternal],
  );

  const logs = useMemo(() => getChangelogs(audience), [audience]);
  const latestRelease = logs[0];

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedType, setSelectedType] = useState<string | null>(null);

  const filterParams = useMemo(
    () => [
      { key: 'type', label: 'Alle Updates', value: null },
      { key: 'type', label: 'Feature', value: 'feature' },
      { key: 'type', label: 'Verbesserung', value: 'improvement' },
      { key: 'type', label: 'Fix', value: 'fix' },
      { key: 'type', label: 'Breaking', value: 'breaking' },
    ],
    [],
  );

  const filteredLogs = useMemo(() => {
    return logs.filter((log) => {
      const matchesSearch =
        log.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        log.description.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesType = !selectedType || log.types.includes(selectedType as any);
      return matchesSearch && matchesType;
    });
  }, [logs, searchQuery, selectedType]);

  return (
    <div className="h-full overflow-y-auto bg-gray-50">
      <PageHeader>
        <div className="flex w-full max-w-5xl flex-col gap-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div className="min-w-0">
              <PageTitle title="Changelog" icon={Rocket} iconColor="text-blue-600" />
            </div>
          </div>
        </div>
      </PageHeader>

      <div className="w-full bg-white border-b border-gray-100">
        <div className="mx-auto w-full max-w-5xl px-4 py-6 sm:px-6 sm:py-8">
          <div className="flex flex-col md:flex-row md:items-start justify-between gap-6">
            <div className="space-y-2 max-w-2xl">
              <div className="flex items-center gap-3">
                <h2 className="text-2xl font-bold tracking-tight text-gray-900">
                  {latestRelease ? latestRelease.title : 'Changelog'}
                </h2>
                {latestRelease && (
                  <div className="flex items-center gap-2">
                    <Badge variant="default" className="rounded-full bg-blue-600 px-2 py-0.5 text-[10px] font-bold text-white shadow-sm hover:bg-blue-600 uppercase tracking-widest">
                      Neu
                    </Badge>
                    <Badge variant="outline" className="rounded-full border-gray-200 bg-gray-50 px-2.5 py-0.5 text-[11px] font-medium text-gray-600 shadow-sm">
                      v{latestRelease.version}
                    </Badge>
                  </div>
                )}
              </div>
              <p className="text-sm leading-relaxed text-gray-500">
                {latestRelease?.description || 'Hier findest du alle relevanten Änderungen der Plattform kompakt zusammengefasst.'}
              </p>
              {latestRelease && (
                <div className="flex flex-wrap items-center gap-1.5 pt-1">
                  {latestRelease.types.map((type) => (
                    <ReleaseTypeBadge key={`${latestRelease.version}-${type}`} type={type} />
                  ))}
                </div>
              )}
            </div>

            <div className="flex items-center gap-6 text-sm shrink-0">
              <div className="flex flex-col gap-1">
                <span className="text-xs font-medium text-gray-500">Updates</span>
                <span className="text-gray-900 font-semibold">{logs.length}</span>
              </div>
              <div className="h-8 w-px bg-gray-100 hidden sm:block"></div>
              <div className="flex flex-col gap-1">
                <span className="text-xs font-medium text-gray-500">Letztes Release</span>
                <span className="text-gray-900 font-semibold">
                  {latestRelease ? format(new Date(latestRelease.date), 'dd.MM.yyyy') : '-'}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="w-full border-b border-gray-100 bg-white">
        <div className="mx-auto w-full max-w-5xl">
          <DataViewToolbar
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            searchPlaceholder="Changelog durchsuchen..."
            filterParams={filterParams}
            selectedFilter={selectedType}
            onFilterChange={setSelectedType}
            filterLabel="Typ filtern"
            hideViewModeToggle={true}
          />
        </div>
      </div>

      <div className="mx-auto flex w-full max-w-5xl flex-col gap-6 px-4 py-6 sm:px-6 sm:py-8">
        {filteredLogs.length === 0 ? (
          <EmptyState />
        ) : (
          <div className="relative space-y-6 before:absolute before:bottom-8 before:left-[31px] before:top-8 before:hidden before:w-px before:bg-gray-100 lg:before:block">
            {filteredLogs.map((entry) => (
              <article key={entry.version} className="relative lg:pl-20">
                <div className="absolute left-0 top-10 hidden lg:flex lg:h-16 lg:w-16 lg:items-center lg:justify-center">
                  <div className="flex h-12 w-12 items-center justify-center rounded-2xl border border-gray-100 bg-white shadow-sm text-gray-500">
                    <Rocket className="h-5 w-5" />
                  </div>
                </div>

                <Card className="overflow-hidden border border-gray-100 bg-white shadow-sm rounded-2xl transition-shadow duration-200 hover:shadow-md">
                  <CardContent className="p-6 sm:p-8">
                    <div className="flex flex-col gap-5">
                      <div className="flex flex-col gap-4 pb-5 sm:flex-row sm:items-start sm:justify-between">
                        <div className="space-y-3">
                          <div className="flex items-center gap-2 lg:hidden">
                            <div className="flex h-10 w-10 items-center justify-center rounded-xl border border-gray-100 bg-white shadow-sm text-gray-500">
                              <Rocket className="h-4 w-4" />
                            </div>
                            <Badge
                              variant="outline"
                              className="rounded-full border-gray-200 bg-gray-50 px-3 py-1 text-xs font-semibold text-gray-700"
                            >
                              Version {entry.version}
                            </Badge>
                          </div>

                          <div className="hidden lg:block">
                            <Badge
                              variant="outline"
                              className="rounded-full border-gray-200 bg-gray-50 px-3 py-1 text-xs font-semibold text-gray-700"
                            >
                              Version {entry.version}
                            </Badge>
                          </div>

                          <div>
                            <h2 className="text-xl font-semibold tracking-tight text-gray-900 sm:text-2xl">
                              {entry.title}
                            </h2>
                            <p className="mt-2 max-w-3xl text-sm leading-relaxed text-gray-500">
                              {entry.description}
                            </p>
                          </div>
                        </div>

                        <div className="flex flex-col gap-3 sm:items-end">
                          <time
                            dateTime={entry.date}
                            className="text-xs font-medium uppercase tracking-[0.16em] text-gray-400"
                          >
                            {format(new Date(entry.date), 'dd. MMMM yyyy', { locale: de })}
                          </time>

                          <div className="flex flex-wrap gap-2 sm:justify-end">
                            {entry.types.map((type) => (
                              <ReleaseTypeBadge key={`${entry.version}-${type}`} type={type} />
                            ))}
                          </div>
                        </div>
                      </div>

                      <div className="space-y-5">
                        <ReactMarkdown components={RELEASE_CARD_MARKDOWN}>
                          {entry.resolvedContent || ''}
                        </ReactMarkdown>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </article>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ChangelogPage;
